import React from "react";
import { Box,Paper } from "@mui/material";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { COLORS } from "../../../assets/theme";
import CustomTable from "../../../components/table/CustomTable";
import SearchHeaderContainer from "../../../components/table/SearchHeaderContainer";
import CustomLabel from "../../../components/CustomLabel";
import PageHeader from "../../../components/PageHeader";
import { useParams } from "react-router-dom";
import { getBalanceSheet } from "../../../services/newReportsService";
import { getAllBranches } from "../../../services/BranchesService";
import RevenueExpenseChart from "../reports/RevenueExpenseChart";
import { getDailyRevenue, getExpensesDaily } from "../../../services/newReportsService";

const BalanceSheetReport: React.FC = () => {
  const params = useParams<{ businessId?: string; id?: string }>();
  const businessId = params.businessId || params.id;

  if (!businessId) {
    throw new Error("Balance Sheet report requires businessId in route");
  }

  const [rows, setRows] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [units, setUnits] = React.useState<any[]>([]);
  const [selectValue, setSelectValue] = React.useState<string | number>("all");

  const [dateRange, setDateRange] = React.useState<
    { start?: string; end?: string } | null
  >(() => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
    return { start: start.toISOString(), end: end.toISOString() };
  });

  const buildDateBoundary = (raw: string | Date | null | undefined, endOfDay: boolean) => {
    if (!raw) return null;
    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split("-").map(Number);
      return new Date(Date.UTC(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0));
    }
    const parsed = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(
      Date.UTC(
        parsed.getUTCFullYear(),
        parsed.getUTCMonth(),
        parsed.getUTCDate(),
        endOfDay ? 23 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 999 : 0,
      ),
    );
  };

  const normalizeRangeIso = (range: { start?: string; end?: string } | null) => {
    if (!range) return { from: undefined as string | undefined, to: undefined as string | undefined };
    const fromDate = buildDateBoundary(range.start, false);
    const toDate = buildDateBoundary(range.end, true);
    return {
      from: fromDate ? fromDate.toISOString() : undefined,
      to: toDate ? toDate.toISOString() : undefined,
    };
  };

  const getUnitId = (u: any) => u.id ?? u.unit_id;
const [summary, setSummary] = React.useState<{
  assets: number;
  liabilities: number;
  equity: number;
  revenue: number;
  expenses: number;
  retainedEarnings: number;
} | null>(null);
const [revenueData, setRevenueData] = React.useState<
  { date: string; revenue: number; expenses: number }[]
>([]);


  // Fetch Units
  React.useEffect(() => {
    const fetchUnits = async () => {
      try {
        const res = await getAllBranches(businessId);
        const unitsData = res?.units || res?.data || res || [];
        setUnits(Array.isArray(unitsData) ? unitsData : []);
      } catch {
        setUnits([]);
      }
    };

    fetchUnits();
  }, [businessId]);

  // Fetch Balance Sheet
  React.useEffect(() => {
    const fetchBalance = async () => {
      setLoading(true);

      try {
        const { from, to } = normalizeRangeIso(dateRange);
        const params: any = {
          businessId,
          from,
          to,
        };

        if (selectValue !== "all") {
          params.unitId = String(selectValue);
        }

        type BalanceSheetResponse = {
  assets: number;
  liabilities: number;
  equity: number;
  revenue: number;
  expenses: number;
  retainedEarnings: number;
};

// ...existing code...

const res = await getBalanceSheet(params) as BalanceSheetResponse;
setSummary(res);
const data = [
  { label: "Assets", value: res?.assets ?? 0 },
  { label: "Liabilities", value: res?.liabilities ?? 0 },
  { label: "Equity", value: res?.equity ?? 0 },
  { label: "Revenue", value: res?.revenue ?? 0 },
  { label: "Expenses", value: res?.expenses ?? 0 },
  { label: "Retained Earnings", value: res?.retainedEarnings ?? 0 },
];

        setRows(data);
      } catch (err) {
        console.error("Failed to fetch Balance Sheet:", err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchBalance();
  }, [businessId, selectValue, dateRange]);
type DailyTotal = {
  date: string;
  total: number;
};
React.useEffect(() => {
  const loadChart = async () => {
    try {
      const revenue = (await getDailyRevenue({
        businessId,
        from: dateRange?.start,
        to: dateRange?.end,
        unitId: selectValue !== "all" ? String(selectValue) : undefined,
      })) as DailyTotal[];

      const expenses = (await getExpensesDaily({
        businessId,
        from: dateRange?.start,
        to: dateRange?.end,
        unitId: selectValue !== "all" ? String(selectValue) : undefined,
      })) as DailyTotal[];

      const merged = revenue.map((r) => {
        const exp = expenses.find((e) => e.date === r.date);
        return {
          date: r.date,
          revenue: r.total,
          expenses: exp?.total || 0,
        };
      });

      setRevenueData(merged);
    } catch (err) {
      console.error("Failed to load revenue/expense chart", err);
    }
  };

  loadChart();
}, [businessId, selectValue, dateRange]);

  const exportCsv = React.useCallback(() => {
    if (!rows.length) return;

    const keys = ["label", "value"];
    const header = keys.join(",");
    const lines = rows.map((r) => {
      const row: Record<string, any> = {
        label: r.label ?? "-",
        value: r.value ?? "-",
      };
      return keys.map((k) => `"${String(row[k] ?? "-").replace(/"/g, '""')}"`).join(",");
    });

    const csv = [header, ...lines].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `balance-sheet-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [rows]);

  return (
    <Box sx={{ width: "100%", maxWidth: "90vw", overflowX: "hidden" }}>
      <PageHeader title="Balance Sheet" sx={{ mt: { xs: 2, sm: 3 } }} />
{/* Financial Summary Cards */}
{summary && (
  <Box sx={{ mb: 3 }}>
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: 2,
      }}
    >
      {[
        { label: "Revenue", value: summary.revenue },
        { label: "Expenses", value: summary.expenses },
        { label: "Assets", value: summary.assets },
        { label: "Liabilities", value: summary.liabilities },
        { label: "Equity", value: summary.equity },
        { label: "Net Profit", value: summary.revenue - summary.expenses },
      ].map((item) => (
        <Box
          key={item.label}
          sx={{
            p: 2,
            borderRadius: 2,
            background: "#fff",
            boxShadow: "0 1px 4px rgba(0,0,0,0.1)",
          }}
        >
          <CustomLabel
            text={item.label}
            variant="body2"
            sx={{ color: "text.secondary" }}
          />
          <CustomLabel
            text={`$${item.value}`}
            variant="h6"
            sx={{ fontWeight: "bold" }}
          />
        </Box>
      ))}
    </Box>
  </Box>
)}
      <SearchHeaderContainer
        search={search}
        setSearch={setSearch}
        dateRangeConfig={{
          value: dateRange ?? undefined,
          onChange: (v: any) => setDateRange(v),
        }}
        selectConfig={{
          options: [
            { label: "All", value: "all" },
            ...units.map((u) => ({
              label: u.name || "Unknown Branch",
              value: getUnitId(u),
            })),
          ],
          value: selectValue,
          onChange: (v: any) => setSelectValue(v),
          label: "Select Branch",
        }}
        buttonConfig={[
          {
            label: "Export CSV",
            onClick: () => Promise.resolve(exportCsv()),
            startIcon: <FileDownloadIcon />,
            bgColor: COLORS.MainColor,
            textColor: "#fff",
          },
        ]}
      />

      <CustomTable<any>
        filters={{ page: 0, size: 10, search }}
        clientSidePagination
        loading={loading}
        fields={[
          { key: "label", header: "Type" },
          { key: "value", header: "Amount" },
        ]}
        fetchFunc={async (params?: any) => {
          const q = String(params?.search || search || "")
            .toLowerCase()
            .trim();

          const filtered = rows.filter((r) => {
            if (q) {
              const text = `${r.label} ${r.value}`.toLowerCase();
              if (!text.includes(q)) return false;
            }
            return true;
          });

          return {
            content: filtered,
            number: 0,
            size: params?.size || 10,
            totalElements: filtered.length,
            totalPages: Math.max(
              1,
              Math.ceil(filtered.length / (params?.size || 10))
            ),
          };
        }}
      />
      <Paper sx={{ p: 3, mb: 3 }}>
  <CustomLabel text="Revenue vs Expenses" variant="h6" sx={{ mb: 2 }} />
  <RevenueExpenseChart data={revenueData} />
</Paper>
    </Box>
  );
};

export default BalanceSheetReport;
